// src/pages/SecondPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { BaseUrl } from "../BaseUrl";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";

const API_URL = `${BaseUrl}/api/products`;
const ORDER_TABLE_STORAGE_KEY = "rupioo_costing_order_table_rows_v2";
const OPEN_PRODUCT_EDIT_KEY = "rupioo_costing_open_product_edit_id";

const UI = {
  pageBg: "#EEF1F4",
  headerBg: "#131921",
  amazonOrange: "#FF9900",
  amazonOrangeDeep: "#F08804",
  linkBlue: "#007185",
  text: "#111",
  sub: "#565959",
  border: "#D5D9D9",
  cardBg: "#fff",
  danger: "#B12704",
  green: "#007600",
};

function safeStr(v) {
  return (v ?? "").toString();
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function money(v) {
  return num(v).toFixed(2);
}

function qty(v) {
  return num(v).toFixed(2);
}

function weight(v) {
  return num(v).toFixed(3);
}

function readLocalJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeLocalJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function removeLocalJSON(key) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function readSessionJSON(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function removeSessionKeys() {
  try {
    sessionStorage.removeItem("createOrder_products");
    sessionStorage.removeItem("createOrder_product");
  } catch {}
}

function getFirstValue(obj, keys, fallback = "") {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
}

function normalizeProductFromApi(p) {
  return {
    ...p,
    category: p?.category ?? p?.catageory ?? "",
    subCategory:
      p?.subCategory ??
      p?.subcategory ??
      p?.subCatageory ??
      p?.subcatageory ??
      "",
    gstAmount: p?.gstAmount ?? p?.gstRate ?? "",
    totalAmount: p?.totalAmount ?? "",
  };
}

function makeRowKey(product, index) {
  const productId = product?._id || product?.productId || "";
  if (productId) return `product_${productId}`;

  const boardQuality = safeStr(product?.boardQuality).trim();
  const name = safeStr(product?.name).trim();

  return `manual_${boardQuality}_${name}_${index}`;
}

function deriveGstPercent(product, amount, gstAmount, totalAmount) {
  const directPaperGst = num(
    getFirstValue(
      product,
      [
        "paperGstRate",
        "paperGstPercent",
        "gstPercent",
        "gstRatePercent",
        "paper_gst_rate",
      ],
      0,
    ),
  );

  if (directPaperGst > 0) return directPaperGst;

  if (amount > 0 && gstAmount > 0) {
    return (gstAmount / amount) * 100;
  }

  if (amount > 0 && totalAmount > amount) {
    return ((totalAmount - amount) / amount) * 100;
  }

  return 0;
}

function recalculateRow(row) {
  const orderSheetL = num(row?.orderSheetL);
  const orderSheetB = num(row?.orderSheetB);
  const orderSheetGsm = num(row?.orderSheetGsm);
  const quantityValue = num(row?.quantity);
  const rate = num(row?.rate);

  let gstPercent = num(row?.gstPercent);

  // ✅ fallback: if GST percent is missing, derive it from previous amount/gst amount
  if (!gstPercent && num(row?.amount) > 0 && num(row?.gstAmount) > 0) {
    gstPercent = (num(row.gstAmount) / num(row.amount)) * 100;
  }

  const totalWeight =
    ((orderSheetL * orderSheetB * orderSheetGsm) / 10000000) * quantityValue;

  const amount = totalWeight * rate;
  const gstAmount = (amount * gstPercent) / 100;
  const totalAmount = amount + gstAmount;

  return {
    ...row,
    orderSheetL,
    orderSheetB,
    orderSheetGsm,
    quantity: quantityValue,
    rate,
    gstPercent,
    totalWeight,
    amount,
    gstAmount,
    totalAmount,
  };
}

function buildOrderRow(product, index) {
  const p = normalizeProductFromApi(product || {});

  const boardQuality = getFirstValue(
    p,
    ["boardQuality", "board", "quality"],
    "",
  );
  const name = getFirstValue(p, ["name", "productName"], "");

  const orderSheetL = num(
    getFirstValue(p, ["orderSheetL", "sheetL", "order_sheet_l"], 0),
  );

  const orderSheetB = num(
    getFirstValue(p, ["orderSheetB", "sheetB", "order_sheet_b"], 0),
  );

  const orderSheetGsm = num(
    getFirstValue(p, ["orderSheetGsm", "sheetGsm", "order_sheet_gsm"], 0),
  );

  const quantityValue = num(
    getFirstValue(p, ["quantity", "qty", "sheetQuantity"], 0),
  );

  let totalWeight = num(getFirstValue(p, ["totalWeight"], 0));

  if (
    !totalWeight &&
    orderSheetL &&
    orderSheetB &&
    orderSheetGsm &&
    quantityValue
  ) {
    totalWeight =
      ((orderSheetL * orderSheetB * orderSheetGsm) / 10000000) * quantityValue;
  }

  const rate = num(getFirstValue(p, ["rate"], 0));

  let amount = num(getFirstValue(p, ["amount"], 0));

  if (!amount && totalWeight && rate) {
    amount = totalWeight * rate;
  }

  let gstAmount = num(
    getFirstValue(p, ["gstAmount", "gstAmt", "gst_amount"], 0),
  );

  const totalAmountFromProduct = num(
    getFirstValue(p, ["totalAmount", "total_amount"], 0),
  );

  if (!gstAmount && totalAmountFromProduct && amount) {
    gstAmount = totalAmountFromProduct - amount;
  }

  const gstPercent = deriveGstPercent(
    p,
    amount,
    gstAmount,
    totalAmountFromProduct,
  );

  if (!gstAmount && gstPercent) {
    gstAmount = (amount * gstPercent) / 100;
  }

  const totalAmount = totalAmountFromProduct || amount + gstAmount;

  return recalculateRow({
    key: makeRowKey(p, index),
    productId: p?._id || p?.productId || "",
    boardQuality,
    name,
    orderSheetL,
    orderSheetB,
    orderSheetGsm,
    quantity: quantityValue,
    totalWeight,
    rate,
    amount,
    gstAmount,
    totalAmount,
    gstPercent,
    manualOverrides: {},
  });
}

function normalizeStoredRows(rows) {
  if (!Array.isArray(rows)) return [];

  return rows.filter(Boolean).map((row, index) =>
    recalculateRow({
      key: row?.key || makeRowKey(row, index),
      productId: row?.productId || "",
      boardQuality: row?.boardQuality || "",
      name: row?.name || "",
      orderSheetL: row?.orderSheetL ?? 0,
      orderSheetB: row?.orderSheetB ?? 0,
      orderSheetGsm: row?.orderSheetGsm ?? 0,
      quantity: row?.quantity ?? 0,
      totalWeight: row?.totalWeight ?? 0,
      rate: row?.rate ?? 0,
      amount: row?.amount ?? 0,
      gstAmount: row?.gstAmount ?? 0,
      totalAmount: row?.totalAmount ?? 0,
      gstPercent: row?.gstPercent ?? 0,
      manualOverrides: row?.manualOverrides || {},
    }),
  );
}

function applyLatestProductToRow(existingRow, latestProduct, index = 0) {
  const latestRow = buildOrderRow(latestProduct, index);
  const manualOverrides = existingRow?.manualOverrides || {};

  const merged = {
    ...existingRow,
    ...latestRow,
    key: existingRow?.key || latestRow.key,
    productId: existingRow?.productId || latestRow.productId,
    manualOverrides,
  };

  if (manualOverrides.orderSheetGsm) {
    merged.orderSheetGsm = existingRow.orderSheetGsm;
  }

  if (manualOverrides.quantity) {
    merged.quantity = existingRow.quantity;
  }

  if (manualOverrides.rate) {
    merged.rate = existingRow.rate;
  }

  return recalculateRow(merged);
}

function mergeRowsWithIncomingProducts(oldRows, incomingProducts) {
  const map = new Map();

  oldRows.forEach((row) => {
    if (!row?.key) return;
    map.set(row.key, row);
  });

  incomingProducts.forEach((product, index) => {
    const freshRow = buildOrderRow(product, index);
    const existing = map.get(freshRow.key);

    if (existing) {
      map.set(freshRow.key, applyLatestProductToRow(existing, product, index));
    } else {
      map.set(freshRow.key, freshRow);
    }
  });

  return Array.from(map.values());
}

function EditableNumberCell({ value, onChange }) {
  return (
    <TextField
      size="small"
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        width: "100%",
        "& .MuiOutlinedInput-root": {
          height: 32,
          borderRadius: 1.2,
          background: "#fff",
        },
        "& input": {
          textAlign: "right",
          fontWeight: 1000,
          fontSize: 12,
          padding: "6px 5px",
        },
      }}
      inputProps={{
        step: "any",
      }}
    />
  );
}

export default function SecondPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = useState(() =>
    normalizeStoredRows(readLocalJSON(ORDER_TABLE_STORAGE_KEY, [])),
  );

  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
    writeLocalJSON(ORDER_TABLE_STORAGE_KEY, rows);
  }, [rows]);

  const refreshRowsFromLatestProducts = async (baseRows = rowsRef.current) => {
    const currentRows = Array.isArray(baseRows) ? baseRows : [];

    if (!currentRows.length) return;

    try {
      const res = await axios.get(API_URL);
      const products = Array.isArray(res.data)
        ? res.data.map(normalizeProductFromApi)
        : [];

      const byId = new Map();

      products.forEach((p) => {
        const id = p?._id || p?.productId;
        if (id) byId.set(String(id), p);
      });

      const refreshedRows = currentRows.map((row, index) => {
        if (!row?.productId) return row;

        const latestProduct = byId.get(String(row.productId));
        if (!latestProduct) return row;

        return applyLatestProductToRow(row, latestProduct, index);
      });

      setRows(refreshedRows);
    } catch {
      // Keep old saved rows if product refresh fails.
    }
  };

  useEffect(() => {
    const fromStateProducts = Array.isArray(location?.state?.products)
      ? location.state.products
      : [];

    const fromStateSingle = location?.state?.product
      ? [location.state.product]
      : [];

    const fromSessionProducts = readSessionJSON("createOrder_products", []);
    const fromSessionSingle = readSessionJSON("createOrder_product", null);

    const incoming =
      fromStateProducts.length > 0
        ? fromStateProducts
        : Array.isArray(fromSessionProducts) && fromSessionProducts.length > 0
          ? fromSessionProducts
          : fromStateSingle.length > 0
            ? fromStateSingle
            : fromSessionSingle
              ? [fromSessionSingle]
              : [];

    const currentRows = rowsRef.current;

    const mergedRows =
      incoming.length > 0
        ? mergeRowsWithIncomingProducts(currentRows, incoming)
        : currentRows;

    if (incoming.length > 0) {
      setRows(mergedRows);
    }

    refreshRowsFromLatestProducts(mergedRows);
    removeSessionKeys();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    const refreshOnFocus = () => {
      refreshRowsFromLatestProducts(rowsRef.current);
    };

    const refreshOnVisible = () => {
      if (document.visibilityState === "visible") {
        refreshRowsFromLatestProducts(rowsRef.current);
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnVisible);

    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnVisible);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.quantity += num(row.quantity);
        acc.totalWeight += num(row.totalWeight);
        acc.amount += num(row.amount);
        acc.gstAmount += num(row.gstAmount);
        acc.totalAmount += num(row.totalAmount);
        return acc;
      },
      {
        quantity: 0,
        totalWeight: 0,
        amount: 0,
        gstAmount: 0,
        totalAmount: 0,
      },
    );
  }, [rows]);

  const updateEditableField = (rowKey, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) return row;

        const updatedRow = {
          ...row,
          [field]: value,
          manualOverrides: {
            ...(row.manualOverrides || {}),
            [field]: true,
          },
        };

        // ✅ Immediate recalculation whenever GSM / Quantity / Rate changes
        return recalculateRow(updatedRow);
      }),
    );
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const clearOrder = () => {
    setRows([]);
    removeLocalJSON(ORDER_TABLE_STORAGE_KEY);
  };

  const openProductInHomeEdit = (row) => {
    if (!row?.productId) {
      alert("Product ID not found for this row.");
      return;
    }

    try {
      sessionStorage.setItem(OPEN_PRODUCT_EDIT_KEY, String(row.productId));
    } catch {}

    navigate(-1);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: UI.pageBg,
      }}
    >
      {/* Single static header */}
      <Box
        sx={{
          width: "100%",
          background: UI.headerBg,
          color: "white",
        }}
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              px: { xs: 1, md: 2 },
              py: 1.1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Tooltip title="Back to Home" arrow>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: "white",
                  background: "rgba(255,255,255,0.08)",
                  "&:hover": { background: "rgba(255,255,255,0.14)" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 20 }}>
                Rupioo
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: 16,
                  color: UI.amazonOrange,
                }}
              >
                Order Sheet
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Items: ${rows.length}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />

              <Chip
                label={`Weight: ${weight(totals.totalWeight)}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />

              <Chip
                label={`Amount: ${money(totals.totalAmount)}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddShoppingCartIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  background: UI.amazonOrangeDeep,
                  color: "#111",
                  fontWeight: 1000,
                  "&:hover": { background: UI.amazonOrange },
                }}
              >
                Add More
              </Button>

              <Button
                variant="contained"
                startIcon={<RestartAltIcon />}
                disabled={!rows.length}
                onClick={clearOrder}
                sx={{
                  background: "#FFFFFF",
                  color: "#111",
                  fontWeight: 1000,
                  "&:hover": { background: "#F0F2F2" },
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: "100%",
          px: { xs: 0.5, md: 1.5 },
          py: 1.5,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            width: "100%",
            borderRadius: 2,
            borderColor: UI.border,
            background: UI.cardBg,
            overflow: "visible",
            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              px: 1.2,
              py: 1,
              background: "linear-gradient(90deg,#ffffff,#F7F8FA)",
              borderBottom: `1px solid ${UI.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 1100, color: UI.text, fontSize: 17 }}
              >
                Selected Products Order Table
              </Typography>

              <Typography sx={{ color: UI.sub, fontWeight: 800, fontSize: 12 }}>
                Editing GSM, Quantity or Rate will instantly recalculate Weight,
                Amount, GST and Total.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Rows: ${rows.length}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "#EEF6FF",
                  border: "1px solid #B8D8FF",
                }}
              />

              <Chip
                label={`GST: ${money(totals.gstAmount)}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "#FFF6EC",
                  border: "1px solid #FFD5A6",
                }}
              />
            </Box>
          </Box>

          {rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{ fontWeight: 1000, color: UI.text, fontSize: 18 }}
              >
                No products selected
              </Typography>

              <Typography sx={{ mt: 0.8, color: UI.sub, fontWeight: 800 }}>
                Go back to the Home page, select one or multiple products, then
                click Create Order.
              </Typography>

              <Button
                variant="contained"
                onClick={() => navigate(-1)}
                sx={{
                  mt: 2,
                  background: UI.amazonOrangeDeep,
                  color: "#111",
                  fontWeight: 1000,
                  "&:hover": { background: UI.amazonOrange },
                }}
              >
                Go Back
              </Button>
            </Box>
          ) : (
            <Table
              size="small"
              sx={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headSx, width: "3.5%" }}>S.No</TableCell>

                  <TableCell sx={{ ...headSx, width: "9%" }}>
                    Board Quality
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "15%" }}>Name</TableCell>

                  <TableCell sx={{ ...headSx, width: "7%" }} align="right">
                    Order Sheet L
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "7%" }} align="right">
                    Order Sheet B
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "8%" }} align="right">
                    Order Sheet Gsm
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "8%" }} align="right">
                    Quantity
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "9%" }} align="right">
                    Total Weight
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "7%" }} align="right">
                    Rate
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "8%" }} align="right">
                    Amount
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "8%" }} align="right">
                    GST Amount
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "8.5%" }} align="right">
                    Total Amount
                  </TableCell>

                  <TableCell sx={{ ...headSx, width: "4%" }} align="center">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.key}
                    sx={{
                      "&:nth-of-type(even)": { background: "#FAFAFA" },
                      "&:hover": { background: "#FFF7ED" },
                    }}
                  >
                    <TableCell sx={bodySx}>{index + 1}</TableCell>

                    <TableCell sx={bodySx}>
                      <Box
                        sx={{
                          fontWeight: 1000,
                          color: UI.text,
                          lineHeight: 1.15,
                          wordBreak: "break-word",
                        }}
                      >
                        {row.boardQuality || "—"}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ ...bodySx, fontWeight: 1000 }}>
                      <Typography
                        component="button"
                        onClick={() => openProductInHomeEdit(row)}
                        sx={{
                          border: "none",
                          background: "transparent",
                          p: 0,
                          m: 0,
                          cursor: "pointer",
                          color: UI.green,
                          fontWeight: 1100,
                          fontSize: 12,
                          lineHeight: 1.15,
                          textAlign: "left",
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                          wordBreak: "break-word",
                          "&:hover": {
                            color: "#005f00",
                          },
                        }}
                      >
                        {row.name || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={bodySx} align="right">
                      {qty(row.orderSheetL)}
                    </TableCell>

                    <TableCell sx={bodySx} align="right">
                      {qty(row.orderSheetB)}
                    </TableCell>

                    <TableCell sx={bodySx} align="right">
                      <EditableNumberCell
                        value={row.orderSheetGsm}
                        onChange={(value) =>
                          updateEditableField(row.key, "orderSheetGsm", value)
                        }
                      />
                    </TableCell>

                    <TableCell sx={bodySx} align="right">
                      <EditableNumberCell
                        value={row.quantity}
                        onChange={(value) =>
                          updateEditableField(row.key, "quantity", value)
                        }
                      />
                    </TableCell>

                    <TableCell
                      sx={{ ...bodySx, color: UI.linkBlue }}
                      align="right"
                    >
                      {weight(row.totalWeight)}
                    </TableCell>

                    <TableCell sx={bodySx} align="right">
                      <EditableNumberCell
                        value={row.rate}
                        onChange={(value) =>
                          updateEditableField(row.key, "rate", value)
                        }
                      />
                    </TableCell>

                    <TableCell
                      sx={{ ...bodySx, color: UI.danger }}
                      align="right"
                    >
                      {money(row.amount)}
                    </TableCell>

                    <TableCell
                      sx={{ ...bodySx, color: "#7C3AED" }}
                      align="right"
                    >
                      {money(row.gstAmount)}
                    </TableCell>

                    <TableCell
                      sx={{ ...bodySx, color: UI.green }}
                      align="right"
                    >
                      {money(row.totalAmount)}
                    </TableCell>

                    <TableCell sx={bodySx} align="center">
                      <Tooltip title="Remove row" arrow>
                        <IconButton
                          size="small"
                          onClick={() => removeRow(row.key)}
                          sx={{
                            color: UI.danger,
                            border: `1px solid ${UI.border}`,
                            background: "#fff",
                            width: 30,
                            height: 30,
                            "&:hover": { background: "#FEE2E2" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow sx={{ background: "#131921" }}>
                  <TableCell sx={totalSx} colSpan={3}>
                    TOTAL
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    —
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    —
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    —
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    {qty(totals.quantity)}
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    {weight(totals.totalWeight)}
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    —
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    {money(totals.amount)}
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    {money(totals.gstAmount)}
                  </TableCell>

                  <TableCell sx={totalSx} align="right">
                    {money(totals.totalAmount)}
                  </TableCell>

                  <TableCell sx={totalSx} align="center">
                    —
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

const headSx = {
  background: "#F7F8FA",
  color: UI.text,
  fontWeight: 1100,
  fontSize: 11.5,
  borderBottom: `1px solid ${UI.border}`,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.15,
  px: 0.6,
  py: 0.8,
};

const bodySx = {
  color: UI.text,
  fontWeight: 900,
  fontSize: 12,
  borderBottom: `1px solid ${UI.border}`,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.15,
  px: 0.6,
  py: 0.7,
};

const totalSx = {
  color: "#fff",
  fontWeight: 1100,
  fontSize: 12,
  borderBottom: "none",
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.15,
  px: 0.6,
  py: 0.9,
};
